// §34.3 - pure helpers for the day-detail bay x time grid (client/src/features/
// jobs/scheduling/DayGrid.tsx). Kept dependency-free of React so the slot
// logic is directly unit-testable and so duration-less jobs (no
// estimatedMinutes yet) can recompute open slots locally with the exact same
// truth the server uses (packages/shared/src/scheduling.ts,
// INV-34-AVAIL-TRUTH) instead of a second, drifting implementation.
import type { DayDetail, DaySlots, DaySpan } from '@wrapforce/shared';
import {
  availableBaysForSpan,
  planDaySpans,
  validateMultiDayPlacement,
  type BaySegment,
  type PlacementRejectReason,
  type SchedulingBay,
} from '@wrapforce/shared';

export const SLOT_STEP_MINUTES = 30;

/** Every 30-minute row start (ISO instant) within the day's operating window. */
export function timeRowsForWindow(window: DayDetail['window']): string[] {
  if (!window) return [];
  const start = new Date(window.startsAt).getTime();
  const end = new Date(window.endsAt).getTime();
  const rows: string[] = [];
  for (let t = start; t < end; t += SLOT_STEP_MINUTES * 60_000) rows.push(new Date(t).toISOString());
  return rows;
}

export function addMinutesIso(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString();
}

// ---------------------------------------------------------------------------
// §34.4 multi-day span helpers. The user always drives the duration now, so
// open slots and spans are always recomputed client-side with the shared pure
// functions (packages/shared/src/scheduling.ts, INV-34-AVAIL-TRUTH) rather
// than trusting the server's fixed-duration `openSlots`. `days[0]` is the day
// the user interacts with (picks the start on); the rest are the consecutive
// calendar days a long job can spill into, in order (closed days included, so
// planDaySpans can skip them while still consuming a calendar day).
// ---------------------------------------------------------------------------

function planDaysInput(days: DayDetail[]): Array<{ date: string; window: DayDetail['window'] }> {
  return days.map((d) => ({ date: d.date, window: d.window }));
}

function baysOf(day: DayDetail): SchedulingBay[] {
  return day.bays.map((b) => ({ bayId: b.id, isActive: b.isActive }));
}

/** Every day's already-scheduled segments as a flat list, excluding this job's
 * own segments (a reschedule must not treat its current booking as a
 * conflict). Instants are globally unique across days, so a flat list is safe
 * to overlap-check against any span. */
function segmentsAcross(days: DayDetail[], excludeJobId: string | undefined): BaySegment[] {
  return days.flatMap((d) =>
    d.segments
      .filter((s) => s.jobId !== excludeJobId)
      .map((s) => ({ bayId: s.bayId, kind: s.kind, startsAt: s.startsAt, endsAt: s.endsAt })),
  );
}

/** Per-bay open start times on `days[0]` for a job of `durationMinutes`. A
 * start is open when the resulting multi-day span is placeable in SOME
 * assignment of bays: primary check is the clicked column's own bay across
 * every spanned day; failing that, a multi-day span is still open when every
 * day has at least one free bay (the "split across bays" flow). When the whole
 * job fits in day 1 (one span) there is no split, so this reduces exactly to
 * today's single-bay-per-column logic. */
export function resolveMultiDayOpenSlots(days: DayDetail[], durationMinutes: number): DaySlots[] {
  const day0 = days[0];
  if (!day0) return [];
  const bays = baysOf(day0);
  const activeBays = bays.filter((b) => b.isActive);
  if (!day0.window || durationMinutes <= 0) return activeBays.map((b) => ({ bayId: b.bayId, slots: [] }));

  const segments = segmentsAcross(days, day0.job?.id);
  const planDays = planDaysInput(days);
  const rows = timeRowsForWindow(day0.window);

  return activeBays.map((bay) => {
    const slots = rows.filter((rowIso) => {
      const plan = planDaySpans({ startsAt: rowIso, durationMinutes, days: planDays });
      if (!plan.ok) return false;
      const single = validateMultiDayPlacement(plan.spans.map((s) => ({ ...s, bayId: bay.bayId })), segments, bays);
      if (single.ok) return true;
      if (plan.spans.length < 2) return false;
      return plan.spans.every((s) => availableBaysForSpan(s, segments, bays).length > 0);
    });
    return { bayId: bay.bayId, slots };
  });
}

export function isSlotOpen(openSlots: DaySlots[], bayId: string, slotIso: string): boolean {
  return openSlots.find((s) => s.bayId === bayId)?.slots.includes(slotIso) ?? false;
}

export interface MultiDayPlacementInfo {
  ok: true;
  spans: DaySpan[];
  /** True when the clicked bay is free across every spanned day (no split). */
  singleBayOk: boolean;
  /** Days where the clicked bay is not free, with why (when singleBayOk false). */
  singleBayFailures: Array<{ date: string; bayId: string; reason: PlacementRejectReason }>;
  /** Per span: the bays with capacity for that day (for the split selectors). */
  perDay: Array<{ date: string; startsAt: string; endsAt: string; minutes: number; bays: string[] }>;
}

/** Full placement context for a chosen start + clicked bay: the day spans,
 * whether a single bay covers them all, and the free bays per day for the
 * split flow. Pure so it is directly unit-testable. */
export function multiDayPlacementInfo(
  days: DayDetail[],
  startsAt: string,
  bayId: string,
  durationMinutes: number,
): MultiDayPlacementInfo | { ok: false; reason: 'outside_hours' | 'insufficient_days' } {
  const day0 = days[0];
  if (!day0) return { ok: false, reason: 'insufficient_days' };
  const plan = planDaySpans({ startsAt, durationMinutes, days: planDaysInput(days) });
  if (!plan.ok) return { ok: false, reason: plan.reason };

  const bays = baysOf(day0);
  const segments = segmentsAcross(days, day0.job?.id);
  const single = validateMultiDayPlacement(plan.spans.map((s) => ({ ...s, bayId })), segments, bays);
  const perDay = plan.spans.map((s) => ({
    date: s.date,
    startsAt: s.startsAt,
    endsAt: s.endsAt,
    minutes: s.minutes,
    bays: availableBaysForSpan(s, segments, bays),
  }));

  return {
    ok: true,
    spans: plan.spans,
    singleBayOk: single.ok,
    singleBayFailures: single.ok ? [] : single.failures,
    perDay,
  };
}

/** Index in `days` of the day a span date falls on (closed days are still
 * present in `days`), or -1. */
export function dayIndexOfDate(days: DayDetail[], date: string): number {
  return days.findIndex((d) => d.date === date);
}

/** Minutes-from-midnight using the browser's own local wall clock. The day
 * window's ISO instants are absolute UTC instants; without shipping the
 * shop's IANA timezone down to this endpoint (it isn't part of DayDetail),
 * the client has no way to render/position times in the shop's own
 * timezone, so - matching this app's one other client-side time-of-day
 * rendering (features/calendar/CalendarPage.tsx, which also formats
 * `scheduledStart` in the browser's local zone) - this assumes the viewer is
 * in (or close enough to) the shop's timezone. Used only to position the
 * preferred-time marker line, never to validate a placement. */
export function minutesOfDayLocal(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

/** Row index within `rows` whose local time-of-day is closest to `preferredTime`
 * (minutes from midnight). Returns null when `rows` is empty. */
export function closestRowIndex(rows: string[], preferredTime: number): number | null {
  if (rows.length === 0) return null;
  let bestIndex = 0;
  let bestDiff = Infinity;
  rows.forEach((row, i) => {
    const diff = Math.abs(minutesOfDayLocal(row) - preferredTime);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = i;
    }
  });
  return bestIndex;
}
